import React, { useCallback, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import { Typography } from "@mui/material";

import { StyledFormControl, StyledHubIcon } from "./styles";
import { InputLabel } from "../InputLabel";

const getOptionValue = (opt) => (opt?.id !== undefined ? opt?.id : opt);

const DefaultIcon = ({ ...rest }) => (
  <StyledHubIcon
    icon="ChevronDown"
    sx={{
      marginRight: "-20px",
      marginTop: "3px",
      cursor: "pointer",
    }}
    {...rest}
  />
);

export const HubHookFormSelectV2 = ({
  name = "",
  required = false,
  rules = {},
  disabled = false,
  onChange = () => { },
  onInputChange = () => { },
  isLoading = false,
  placeholder = "",
  label = "",
  helperText = "",
  options = [],
  disableHelperText = true,
  smallLabel = true,
  RenderComponent = null,
  selectDefault = false,
  infoProps = {},
  defaultValue = "",
  defaultInputValue = "",
  formControlProps = {},
  useDefault = true,
  hidden = false,
  fullWidth = true,
  allowNull = false,
  useSetValue = false,
  groupByKey = null,
  defaultOpenGroup = null,
  multiple = false,
  shouldUnregister = true,
  TextFieldProps = {},
  emptyOption = null,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState(defaultInputValue);

  const id = `HubHookField-${name}`;
  const defaultSelectValue = useMemo(
    () =>
      multiple
        ? defaultValue || []
        : defaultValue !== "" && defaultValue !== undefined
          ? defaultValue
          : typeof options[0] === "string"
            ? options[0]
            : options[0]?.id,
    [options, defaultValue, multiple]
  );

  const vpInputProps = isLoading
    ? {
      startAdornment: (
        <InputAdornment position="start">
          <CircularProgress size={24} />
        </InputAdornment>
      ),
    }
    : {};

  const fallbackDefaultValue = selectDefault
    ? multiple
      ? options.map(getOptionValue) || []
      : getOptionValue(options?.[0]) || ""
    : multiple
      ? []
      : "";

  const defaultSelectedValue = !useDefault
    ? multiple
      ? []
      : ""
    : defaultSelectValue !== undefined && defaultSelectValue !== null
      ? defaultSelectValue
      : fallbackDefaultValue;

  const { setValue } = useFormContext();
  const {
    field: { onChange: onControllerChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue: useDefault ? defaultSelectedValue : multiple ? [] : "",
    shouldUnregister,
  });

  const errorMessage = error?.message;
  const defaultHelperText = disableHelperText ? "" : <span>&nbsp;</span>;
  const helperTextMessage = errorMessage || helperText || defaultHelperText;

  const [open, setOpen] = useState(false);

  const changeDep = [
    multiple,
    useSetValue,
    onControllerChange,
    onChange,
    setValue,
    name,
    emptyOption,
  ];

  const handleChange = useCallback((event, newValue) => {
    if (newValue?.__isEmptyOption) {
      emptyOption?.onSelect?.(inputValue);
      return;
    }

    const valueToStore = multiple
      ? newValue.map(getOptionValue)
      : getOptionValue(newValue);

    onControllerChange({ target: { value: valueToStore } });
    onChange({ target: { value: valueToStore } }, valueToStore, newValue);

    if (useSetValue) {
      setValue(name, valueToStore);
    }
  }, changeDep);

  // Flatten grouped options if grouping is used
  const flattenedOptions = useMemo(() => {
    if (!groupByKey) return options;
    return options.slice(); // already flat in grouped render
  }, [options, groupByKey]);

  return (
    <StyledFormControl
      hidden={hidden}
      fullWidth={fullWidth}
      key={options.join("-")}
      variant="outlined"
      {...formControlProps}
    >
      <InputLabel
        label={label}
        required={required}
        id={id}
        smallLabel={smallLabel}
      />
      <Autocomplete
        id={id}
        multiple={multiple}
        groupBy={
          groupByKey
            ? (opt) => groupByKey.split(".").reduce((o, k) => o?.[k], opt)
            : undefined
        }
        getOptionLabel={(option) => option?.label || option.toString()}
        isOptionEqualToValue={(opt, val) =>
          getOptionValue(opt) === getOptionValue(val)
        }
        options={flattenedOptions}
        value={
          multiple
            ? options.filter((opt) => value?.includes(getOptionValue(opt)))
            : options.find((opt) => getOptionValue(opt) === value) || null
        }
        filterOptions={(opts, state) => {
          const filtered = opts.filter((opt) => {
            const label =
              opt.label?.toLowerCase?.() || opt.toString().toLowerCase();
            return label.includes(state.inputValue.toLowerCase());
          });

          if (filtered.length === 0 && emptyOption) {
            return [
              {
                id: null,
                label: emptyOption.label,
                __isEmptyOption: true,
              },
            ];
          }

          return filtered;
        }}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={(e, val) => {
          e?.preventDefault();
          e?.stopPropagation();

          setInputValue(val)
          onInputChange(val)
        }}
        inputValue={inputValue}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={ref}
            error={!!error}
            size="small"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              ...vpInputProps,
              ...TextFieldProps,
              endAdornment: <DefaultIcon />,
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={getOptionValue(option)}>
            {RenderComponent ? (
              <RenderComponent {...option} />
            ) : (
              <Typography sx={{ fontSize: "12px" }}>
                {option.label || option}
              </Typography>
            )}
          </li>
        )}
        {...rest}
      />
      {helperTextMessage && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

HubHookFormSelectV2.propTypes = {
  name: PropTypes.string.isRequired,
  defaultSelectValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
  ]),
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.string])
  ),
  disableHelperText: PropTypes.bool,
  smallLabel: PropTypes.bool,
  RenderComponent: PropTypes.node,
  selectDefault: PropTypes.bool,
  infoProps: PropTypes.object,
  multiple: PropTypes.bool,
};

export default HubHookFormSelectV2;
